import { faSearch } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, CircularProgress, InputAdornment, TextField, Tooltip, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { IAudience } from "audience/model"
import { getAllAudiencesInStore } from "audience/tools/common"
import { CURRENCIES } from "currency/model"
import { getValueInUserCurrency } from "currency/tools/common"
import { getCurrencySymbol } from "form/inventory/tools/common"
import { IForm } from "form/model"
import {
  getFilteredAudiences,
  noticeAndClearAudienceError,
  setAudienceFilters,
  synchronizeNewAudienceSelection
} from "form/tools/audience"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { ListIdCell } from "ui/components/ListIdCell"
import { getDataGridTexts } from "ui/tools/list"
import { SelectionFilter } from "../filter/SelectionFilter"

interface IAudienceListProps {
  form: IForm
}

export const AudienceList = React.memo(({ form }: IAudienceListProps): JSX.Element => {
  const [selectedIds, setSelectedIds] = React.useState<number[]>(form.audience.selectedIds)
  const isNoticingError = React.useRef(false)
  React.useEffect(() => {
    if (form.context.relations.audiencesError) {
      if (!isNoticingError.current) {
        isNoticingError.current = true
        noticeAndClearAudienceError(form)
        setSelectedIds(form.audience.selectedIds)
      }
    } else if (isNoticingError.current) {
      isNoticingError.current = false
    }
  })
  const filteredAudiences = getFilteredAudiences(
    form.audience.filter,
    form.audience.selectFilter,
    form.audience.selectedIds
  )
  const columns: Array<GridColDef<IAudience>> = [
    {
      field: "name",
      headerName: translate("label.name"),
      flex: 3,
      renderCell: params => <ListIdCell label={params.row.name} id={params.row.id} />
    },
    {
      field: "description",
      headerName: translate("label.description"),
      renderCell: params => (
        <Tooltip title={params.row.description || ""}>
          <Typography fontSize=".75rem" sx={{ whiteSpace: "no-wrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {params.row.description}
          </Typography>
        </Tooltip>
      ),
      flex: 4
    },
    {
      field: "agencyName",
      headerName: translate("label.agency"),
      flex: 2
    },
    {
      field: "cpm",
      headerName: `${translate("label.cpm")} (${getCurrencySymbol()})`,
      flex: 1,
      renderCell: params => {
        const cpm: null | number = params.row.cpm
        const currency: null | string = params.row.currency_id
        if (cpm === null || currency === null) {
          return ""
        }
        return (
          <Typography color="textPrimary" fontSize=".8rem">
            {getValueInUserCurrency(currency as CURRENCIES, cpm).toFixed(3)}
          </Typography>
        )
      }
    }
  ]
  return (
    <FlexBox
      vertical
      ai="start"
      jc="start"
      gap
      sx={{
        width: "100%",
        height: "100%",
        p: "1rem",
        border: theme => theme.palette.border,
        borderRadius: ".25rem"
      }}>
      <FlexBox gap sx={{ mt: "1rem", ml: "1rem" }}>
        <Typography color="textPrimary" fontWeight={500}>
          {translate("audience.title")}
        </Typography>
        {form.audience.isSynchronizing ? (
          <CircularProgress size="1.5rem" />
        ) : (
          <Typography color="textSecondary" fontSize=".8rem">
            {selectedIds.length}/{getAllAudiencesInStore().length}
          </Typography>
        )}
      </FlexBox>
      <Box
        sx={{
          width: "2rem",
          mt: "-.4rem",
          ml: "1rem",
          height: 0,
          borderBottom: theme => `3px solid ${theme.palette.primary.main}`
        }}
      />
      <FlexBox
        vertical
        ai="start"
        jc="start"
        gap
        fullWidth
        sx={{
          "p": "1rem",
          "pt": 0,
          "height": "100%",
          "& > *": {
            width: "100%"
          },
          "& .MuiDataGrid-selectedRowCount": {
            visibility: "hidden"
          },
          "& .MuiDataGrid-cell": {
            fontSize: ".8rem",
            fontWeight: 300
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer"
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "inherit"
          },
          "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
            {
              outline: "none !important"
            }
        }}>
        <FlexBox jc="space-between" gap wrap>
          <FlexBox vertical ai="start">
            <Typography color="textSecondary" fontSize=".85rem">
              {translate("audience.help")}
            </Typography>
            <FlexBox sx={{ gap: ".5rem" }}>
              <Typography color="textSecondary" fontSize=".8rem">
                Segment Source
              </Typography>
              <Typography color="textPrimary" fontSize=".8rem" fontWeight={500}>
                Datonics
              </Typography>
            </FlexBox>
          </FlexBox>
          <FlexBox jc="start" gap wrap>
            <TextField
              label={translate("label.filter.name")}
              value={form.audience.filter}
              onChange={event => setAudienceFilters({ name: event.target.value })}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faSearch} size="lg" />
                  </InputAdornment>
                )
              }}
            />
            <SelectionFilter
              value={form.audience.selectFilter}
              onChange={newSelectFilter => setAudienceFilters({ selectFilter: newSelectFilter })}
            />
          </FlexBox>
        </FlexBox>
        <DataGrid
          rows={filteredAudiences}
          checkboxSelection
          columns={columns}
          disableColumnFilter
          disableColumnMenu
          selectionModel={selectedIds}
          keepNonExistentRowsSelected
          initialState={{
            sorting: { sortModel: [{ field: "name", sort: "asc" }] },
            columns: { columnVisibilityModel: { agencyName: false } }
          }}
          rowHeight={40}
          autoPageSize
          onSelectionModelChange={newSelectedIds => {
            synchronizeNewAudienceSelection(newSelectedIds as number[])
            setSelectedIds(newSelectedIds as number[])
          }}
          localeText={getDataGridTexts()}
        />
      </FlexBox>
    </FlexBox>
  )
})
